import { Checkbox, EmptyState, InfoIcon, ModalBottomButtons } from '@bp/ui-components';
import classNames from 'classnames';
import { FieldArray, Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import {
  SortDirection,
  useCreateTeachingLoadFactorMutation,
  useTeachingLoadFactorsQuery,
} from '../../../types/planung-graphql-client-defs';
import { showSuccessToast, showUserErrorToast } from '../../../utils/toast';
import { TeachingLoadFactorType } from '../types';
import styles from './FactorInheritForm.module.scss';
import { FactorInheritFormRow } from './FactorInheritFormRow';

export type FactorInheritFormType = (TeachingLoadFactorType & {
  selected: boolean;
})[];

type FactorInheritFormProps = {
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const FactorInheritForm = ({ onLoad, onClose }: FactorInheritFormProps) => {
  const { t } = useTranslation();
  const currentSchoolYear = useUserConfigContext().selectedSchoolYear;
  const previousSchoolYear = useUserConfigContext().previousSchoolYear;
  const factorContext = useMemorizedCacheTag('TEACHINGLOADFACTOR');

  const [, createTeachingLoadFactor] = useCreateTeachingLoadFactorMutation();

  const [{ data: factorsData }] = useTeachingLoadFactorsQuery({
    variables: {
      where: { schoolYear: { uuid: previousSchoolYear?.uuid } },
      options: {
        sort: [{ active: SortDirection.Desc, factorType: SortDirection.Desc }],
      },
    },
    context: factorContext,
    pause: !previousSchoolYear,
  });

  const lastYearFactors: FactorInheritFormType =
    factorsData?.teachingLoadFactors.map((factor) => {
      return {
        ...factor,
        selected: false,
      };
    }) ?? [];

  const where = (uuid?: string) => ({ where: { node: { uuid } } });

  async function handleSubmit(values: FactorInheritFormType, helper: FormikHelpers<FactorInheritFormType>) {
    onLoad(true);

    const factorsToInherit = values.filter((f) => f.selected);

    let inheritCount = 0;

    for (const factor of factorsToInherit) {
      const { error } = await createTeachingLoadFactor(
        {
          input: {
            factorType: factor.factorType,
            grades: factor.grades,
            gradeGroup: {
              connect: factor.gradeGroup?.map((item) => where(item.uuid)),
            },
            subject: {
              connect: factor.subject.map((item) => where(item.uuid)),
            },
            subjectContainer: {
              connect: factor.subjectContainer.map((item) => where(item.uuid)),
            },
            timeGridEntries: {
              connect: factor.timeGridEntries.map((item) => where(item.uuid)),
            },
            teachers: {
              connect: factor.teachers.map((item) => where(item.uuid)),
            },
            value: factor.value,
            valueRaw: factor.valueRaw,
            valueMode: factor.valueMode,
            comment: factor.comment,
            active: factor.active,
            valuePartial: factor.valuePartial,
            valuePartialRaw: factor.valuePartialRaw,
            schoolYear: { connect: where(currentSchoolYear?.uuid) },
          },
        },
        factorContext,
      );

      if (error) {
        showUserErrorToast({ error });
        break;
      } else {
        inheritCount++;
      }
    }

    if (inheritCount > 0) {
      showSuccessToast(t('factors.inheritSuccess', { count: inheritCount }));
    }

    onLoad(false);
    helper.resetForm();
    onClose();
  }

  return !previousSchoolYear || lastYearFactors.length === 0 ? (
    <EmptyState
      title={!previousSchoolYear ? t('common.noPreviousYear') : t('factors.noPreviousFactors')}
      icon={<InfoIcon />}
    />
  ) : (
    <Formik<FactorInheritFormType> initialValues={lastYearFactors} onSubmit={handleSubmit}>
      {({ values, isSubmitting, resetForm, setValues }) => {
        return (
          <Form className={styles['factor-inherit-form']}>
            <div className={styles.header}>
              <div className={styles.checkbox}>
                <Checkbox
                  name='select-all'
                  checked={values.filter((f) => f.selected).length === values.length}
                  onChange={async () => {
                    let selected = false;
                    if (values.filter((f) => f.selected).length === values.length) {
                      selected = false;
                    } else {
                      selected = true;
                    }
                    await setValues((values) =>
                      values.map((f) => {
                        return { ...f, selected };
                      }),
                    );
                  }}
                />
              </div>
              <div className={styles.small}>{t('common.type')}</div>
              <div className={styles.small}>{t('classes.level.full')}</div>
              <div className={styles.small}>{t('gradeGroups.title', { count: 1 })}</div>
              <div className={styles.large}>{t('subject.name')}</div>
              <div className={styles.medium}>{t('subjectContainer.title', { count: 1 })}</div>
              <div className={styles.small}>{t('common.timeGrids')}</div>
              <div className={styles.small}>{t('persons.title', { count: 1 })}</div>
              <div className={styles.small}>{t('factors.title', { count: 1 })}</div>
              <div className={styles.small}>{t('factors.titlePartial')}</div>
              <div className={styles.comment}>{t('common.comment')}</div>
            </div>

            <div className={classNames(styles.body, 'has-scrollbar')}>
              <FieldArray
                name='factors'
                render={(arrayHelpers) => {
                  return values.map((factor, index) => (
                    <FactorInheritFormRow key={factor.uuid} index={index} helper={arrayHelpers} />
                  ));
                }}
              />
            </div>

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              submitButton={{ disabled: isSubmitting || values.filter((f) => f.selected).length === 0 }}
              className='px-6'
            />
          </Form>
        );
      }}
    </Formik>
  );
};
